import { createContext, useEffect, useState } from 'react';
import jwt_decode from 'jwt-decode';
import { useNavigate } from 'react-router-dom';

import axios from './axios';

const AuthContext = createContext({});

const getStoredAuthTokens = () => {
    try {
        const storedTokens = localStorage.getItem('authTokens');
        return storedTokens ? JSON.parse(storedTokens) : null;
    } catch (error) {
        localStorage.removeItem('authTokens');
        return null;
    }
};

const decodeUserFromTokens = (tokens) => {
    try {
        return tokens?.access ? jwt_decode(tokens.access) : null;
    } catch (error) {
        localStorage.removeItem('authTokens');
        return null;
    }
};

export const AuthProvider = ({ children }) => {
    const [authTokens, setAuthTokens] = useState(getStoredAuthTokens);
    const [user, setUser] = useState(() =>
        decodeUserFromTokens(getStoredAuthTokens())
    );

    const navigate = useNavigate();

    const loginUser = async (requestBody) => {
        const response = await axios.post(
            '/api/token/',
            JSON.stringify(requestBody),
            {
                headers: { 'Content-Type': 'application/json' },
            }
        );
        const data = response.data;

        if (response.status === 200) {
            setAuthTokens(data);
            setUser(jwt_decode(data.access));
            localStorage.setItem('authTokens', JSON.stringify(data));
            navigate('/');
        }
    };

    const logoutUser = () => {
        setAuthTokens(null);
        setUser(null);
        localStorage.removeItem('authTokens');
        navigate('/login');
    };

    const updateUser = (updates) => {
        setUser((currentUser) => ({
            ...currentUser,
            ...updates,
        }));
    };

    const replaceAuthTokens = (tokens) => {
        setAuthTokens(tokens);
        setUser(jwt_decode(tokens.access));
        localStorage.setItem('authTokens', JSON.stringify(tokens));
        axios.defaults.headers.post['Authorization'] = 'Bearer ' + tokens.access;
    };

    const updateToken = async () => {
        const response = await axios.post(
            'api/token/refresh/',
            JSON.stringify({ refresh: authTokens?.refresh }),
            {
                headers: { 'Content-Type': 'application/json' },
            }
        );
        const data = response.data;

        if (response.status === 200) {
            setAuthTokens(data);
            setUser(jwt_decode(data.access));
            localStorage.setItem('authTokens', JSON.stringify(data));
            axios.defaults.headers.post['Authorization'] =
                'Bearer ' + data.access;
        } else {
            logoutUser();
        }
    };

    const contextData = {
        user: user,
        authTokens: authTokens,
        loginUser: loginUser,
        logoutUser: logoutUser,
        updateUser: updateUser,
        replaceAuthTokens: replaceAuthTokens,
    };

    useEffect(() => {
        // on load, check if auth tokens are expired. if they are, call updateToken
        if (authTokens) {
            axios.defaults.headers.post['Authorization'] =
                'Bearer ' + authTokens.access;
            const decodedUser = decodeUserFromTokens(authTokens);
            if (decodedUser && Date.now() >= decodedUser.exp * 1000) {
                updateToken();
            }
        }

        // set an automatic interval to refresh the access token very 15 minutes
        const interval = setInterval(
            () => {
                if (authTokens) {
                    updateToken();
                }
            },
            1000 * 60 * 15 // 15 minutes
        );
        return () => clearInterval(interval);
    });

    return (
        <AuthContext.Provider value={contextData}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
